import React, { useState, useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { createPipeline, linkGoogleAccount, listPipelines, getPipeline } from '../clients/grpc';
import { CreatePipelineRequest, Pipeline, Triggers, GmailTrigger, Routes, DefaultRoute, GenreToEmailRoute, ListPipelinesRequest, GetPipelineRequest } from '../generated/v1alpha1/pipelines_pb';
import { LinkGoogleAccountRequest } from '../generated/v1alpha1/auth_pb';
import './PipelinesPage.css';

const PipelinesPage: React.FC = () => {
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // State for the create form
  const [labels, setLabels] = useState('');
  const [defaultRouteEmails, setDefaultRouteEmails] = useState('');
  const [genreRoutes, setGenreRoutes] = useState([{ genres: '', emails: '' }]);
  const [isGmailLinked, setIsGmailLinked] = useState(false);
  const [watchEmail, setWatchEmail] = useState<string | null>(null);

  useEffect(() => {
    fetchPipelines();
  }, []);

  const fetchPipelines = async () => {
    try {
      const req = new ListPipelinesRequest();
      const res = await listPipelines(req);
      setPipelines(res.getPipelinesList());
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unknown error occurred while fetching pipelines.');
      }
    }
  };

  const handlePipelineClick = async (pipelineName: string) => {
    try {
      const req = new GetPipelineRequest();
      req.setName(pipelineName);
      const res = await getPipeline(req);
      setSelectedPipeline(res);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unknown error occurred while fetching pipeline details.');
      }
    }
  };

  const linkGmail = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      const req = new LinkGoogleAccountRequest();
      req.setAuthorizationCode(codeResponse.code);
      try {
        const res = await linkGoogleAccount(req);
        if (res.getSuccess()) {
          setIsGmailLinked(true);
          setWatchEmail(res.getGoogleEmail());
          setMessage('Gmail account linked successfully!');
          setError(null);
        } else {
          setError(res.getMessage());
          setMessage(null);
        }
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unknown error occurred while linking the Gmail account.');
        }
        setMessage(null);
      }
    },
    onError: () => {
      setError('Failed to link Gmail account. Please try again.');
      setMessage(null);
    },
    scope: 'https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.compose https://www.googleapis.com/auth/gmail.modify',
    flow: 'auth-code',
  });

  const handleGenreRouteChange = (index: number, field: 'genres' | 'emails', value: string) => {
    const newGenreRoutes = [...genreRoutes];
    newGenreRoutes[index][field] = value;
    setGenreRoutes(newGenreRoutes);
  };

  const addGenreRoute = () => {
    setGenreRoutes([...genreRoutes, { genres: '', emails: '' }]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!isGmailLinked) {
      setError('Please link your Gmail account before creating a pipeline.');
      return;
    }

    const pipeline = new Pipeline();
    const triggers = new Triggers();
    const gmailTrigger = new GmailTrigger();
    gmailTrigger.setLabelsList(labels.split(',').map(l => l.trim()).filter(l => l));
    if (watchEmail) {
      gmailTrigger.setWatchEmail(watchEmail);
    }
    triggers.setGmail(gmailTrigger);
    pipeline.setTriggers(triggers);

    const routes = new Routes();
    const defaultRoute = new DefaultRoute();
    defaultRoute.setTargetEmailsList(defaultRouteEmails.split(',').map(e => e.trim()).filter(e => e));
    routes.setDefaultRoute(defaultRoute);

    genreRoutes.forEach(route => {
      if (route.genres && route.emails) {
        const genreToEmailRoute = new GenreToEmailRoute();
        genreToEmailRoute.setGenresList(route.genres.split(',').map(g => g.trim()).filter(g => g));
        genreToEmailRoute.setTargetEmailsList(route.emails.split(',').map(e => e.trim()).filter(e => e));
        routes.addGenreToEmail(genreToEmailRoute);
      }
    });

    pipeline.setRoutes(routes);

    const req = new CreatePipelineRequest();
    req.setPipeline(pipeline);

    try {
      const res = await createPipeline(req);
      setMessage(`Pipeline created successfully with name: ${res.getName()}${watchEmail ? ` for Gmail account: ${watchEmail}` : ''}`);
      setShowCreateForm(false);
      fetchPipelines(); // Refresh the list
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unknown error occurred while creating the pipeline.');
      }
    }
  };

  const renderCreateForm = () => (
    <div className="create-pipeline-form">
      <h2>Create New Pipeline</h2>
      <p>Automate your script processing by creating a pipeline that watches your Gmail for new screenplay PDFs, generates screenplay coverage, and optionally routes to other emails.</p>
      
      {error && <p className="error">{error}</p>}
      {message && <p className="message">{message}</p>}

      {!isGmailLinked ? (
        <div className="link-gmail-section">
          <p>Step 1: Link your Gmail account to allow access to read and compose emails.</p>
          <button onClick={() => linkGmail()} className="google-login-button">
            Link Gmail Account
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Gmail Labels (comma-separated)</label>
            <input
              type="text"
              value={labels}
              onChange={(e) => setLabels(e.target.value)}
              placeholder="e.g. scripts, submissions"
              required
            />
          </div>
          <div className="form-group">
            <label>Default Email Routes (comma-separated)</label>
            <input
              type="text"
              value={defaultRouteEmails}
              onChange={(e) => setDefaultRouteEmails(e.target.value)}
              placeholder="e.g. reader1@gmail.com, reader2@gmail.com"
              required
            />
          </div>

          <div className="form-group">
            <label>Genre-based Email Routes</label>
            {genreRoutes.map((route, index) => (
              <div key={index} className="genre-route-pair">
                <input
                  type="text"
                  value={route.genres}
                  onChange={(e) => handleGenreRouteChange(index, 'genres', e.target.value)}
                  placeholder="Genres (e.g. comedy, drama)"
                />
                <input
                  type="text"
                  value={route.emails}
                  onChange={(e) => handleGenreRouteChange(index, 'emails', e.target.value)}
                  placeholder="Emails (e.g. comedy-readers@gmail.com)"
                />
              </div>
            ))}
            <button type="button" onClick={addGenreRoute} className="add-genre-route-button">
              Add Genre Route
            </button>
          </div>

          <button type="submit" className="create-pipeline-button">Create Pipeline</button>
        </form>
      )}
    </div>
  );

  const renderPipelineDetails = (pipeline: Pipeline) => (
    <div className="pipeline-details">
      <h2>{pipeline.getName()}</h2>
      <p><strong>Created by:</strong> {pipeline.getCreatedBy()}</p>
      <p><strong>Status:</strong> {pipeline.getStatus() === Pipeline.Status.STATUS_ENABLED ? 'Enabled' : 'Disabled'}</p>
      {/* Render more details as needed */}
      <button onClick={() => setSelectedPipeline(null)}>Back to list</button>
    </div>
  );

  return (
    <div className="pipelines-container">
      <h1>Pipelines</h1>
      {error && <p className="error">{error}</p>}
      
      <button onClick={() => setShowCreateForm(!showCreateForm)}>
        {showCreateForm ? 'Cancel' : 'Create New Pipeline'}
      </button>

      {showCreateForm ? (
        <div className="create-pipeline-container">
          {renderCreateForm()}
        </div>
      ) : selectedPipeline ? (
        renderPipelineDetails(selectedPipeline)
      ) : (
        <div className="pipeline-list">
          {pipelines.map(p => (
              <div key={p.getName()} className="pipeline-item" onClick={() => handlePipelineClick(p.getName())}>
              {(() => {
                let pipelineT = "Script coverage";
                if (p.getPipelineType() === 0) {
                  pipelineT = "Undefined";
                }
                return <h3>{pipelineT}</h3>;
              })()}
              {p.getTriggers()?.getGmail() && (
                <div>
                  <h4>Trigger: Gmail</h4>
                  <p><strong>Watch Email:</strong> {p.getTriggers()?.getGmail()?.getWatchEmail() || 'N/A'}</p>
                  <p><strong>Labels:</strong> {p.getTriggers()?.getGmail()?.getLabelsList().join(', ') || 'N/A'}</p>
                </div>
              )}
              {p.getRoutes() && (
                <div>
                  <h4>Routes</h4>
                  {p.getRoutes()?.getDefaultRoute() && (
                    <p><strong>Default Route:</strong> {p.getRoutes()?.getDefaultRoute()?.getTargetEmailsList().join(', ') || 'N/A'}</p>
                  )}
                  {p.getRoutes()?.getGenreToEmailList() && p.getRoutes()!.getGenreToEmailList().length > 0 && (
                    <div>
                      <strong>Genre Routes:</strong>
                      <ul>
                        {p.getRoutes()?.getGenreToEmailList().map((route, index) => (
                          <li key={index}>
                            {route.getGenresList().join(', ')} {'->'} {route.getTargetEmailsList().join(', ')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              <p><strong>Status:</strong> {p.getStatus() === Pipeline.Status.STATUS_ENABLED ? 'Enabled' : 'Disabled'}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PipelinesPage;
